import {
  generateImageUploadThumbnail,
  isImageMediaFile,
  isVideoMediaFile,
  shouldGenerateMediaUploadThumbnail,
  type MediaUploadThumbnailResult,
} from '@cartwhl/common/utils/mediaUploadThumbnail';
import type { FilesListFile } from '@cartwhl/ui/input/FilesList';

import { generateVideoUploadThumbnail } from './generateVideoUploadThumbnail';

type ThumbnailCacheEntry = {
  file: File;
  status: 'pending' | 'ready' | 'error';
  result: MediaUploadThumbnailResult | null;
  error: Error | null;
  abortController: AbortController;
  waiters: Array<{
    resolve: (value: MediaUploadThumbnailResult | null) => void;
    reject: (reason?: unknown) => void;
  }>;
};

const cache = new Map<string, ThumbnailCacheEntry>();
let activeVideoJobs = 0;
const videoJobQueue: Array<() => void> = [];

const MAX_CONCURRENT_VIDEO_JOBS = 1;

function notifyWaiters(entry: ThumbnailCacheEntry) {
  for (const waiter of entry.waiters) {
    if (entry.status === 'error') {
      waiter.resolve(null);
      continue;
    }

    waiter.resolve(entry.result);
  }
  entry.waiters.length = 0;
}

function releaseEntry(fileId: string) {
  const entry = cache.get(fileId);
  if (!entry) return;

  entry.abortController.abort();
  notifyWaiters(entry);
  cache.delete(fileId);
}

function runNextVideoJob() {
  if (activeVideoJobs >= MAX_CONCURRENT_VIDEO_JOBS) return;
  const next = videoJobQueue.shift();
  if (!next) return;
  next();
}

async function runVideoThumbnailJob(
  file: File,
  signal: AbortSignal,
): Promise<MediaUploadThumbnailResult> {
  if (signal.aborted) {
    throw new DOMException('Thumbnail generation aborted', 'AbortError');
  }

  return new Promise<MediaUploadThumbnailResult>((resolve, reject) => {
    const start = async () => {
      if (signal.aborted) {
        reject(new DOMException('Thumbnail generation aborted', 'AbortError'));
        runNextVideoJob();
        return;
      }

      activeVideoJobs += 1;
      try {
        const result = await generateVideoUploadThumbnail(file, signal);
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        activeVideoJobs -= 1;
        runNextVideoJob();
      }
    };

    if (activeVideoJobs < MAX_CONCURRENT_VIDEO_JOBS) {
      void start();
      return;
    }

    videoJobQueue.push(() => {
      void start();
    });
  });
}

async function generateThumbnailForFile(
  file: File,
  signal: AbortSignal,
): Promise<MediaUploadThumbnailResult> {
  if (isImageMediaFile(file)) {
    return generateImageUploadThumbnail(file);
  }

  if (isVideoMediaFile(file)) {
    return runVideoThumbnailJob(file, signal);
  }

  throw new Error('Unsupported media type for thumbnail generation');
}

function scheduleThumbnailGeneration(fileId: string, file: File) {
  const existing = cache.get(fileId);
  if (existing && existing.file === file) {
    return;
  }

  if (existing) {
    releaseEntry(fileId);
  }

  const abortController = new AbortController();
  const entry: ThumbnailCacheEntry = {
    file,
    status: 'pending',
    result: null,
    error: null,
    abortController,
    waiters: [],
  };

  cache.set(fileId, entry);

  void generateThumbnailForFile(file, abortController.signal)
    .then((result) => {
      const current = cache.get(fileId);
      if (!current || current !== entry) return;

      entry.status = 'ready';
      entry.result = result;
      notifyWaiters(entry);
    })
    .catch((error: unknown) => {
      const current = cache.get(fileId);
      if (!current || current !== entry) return;

      if (
        error instanceof DOMException &&
        error.name === 'AbortError'
      ) {
        return;
      }

      entry.status = 'error';
      entry.error =
        error instanceof Error
          ? error
          : new Error('Failed to generate upload thumbnail');
      console.warn('[mediaUploadThumbnailCache]', entry.error);
      notifyWaiters(entry);
    });
}

export function syncMediaUploadThumbnailJobs(files: FilesListFile[]) {
  const activeIds = new Set(files.map((file) => file.id));

  for (const fileId of cache.keys()) {
    if (!activeIds.has(fileId)) {
      releaseEntry(fileId);
    }
  }

  for (const { id, file } of files) {
    if (!shouldGenerateMediaUploadThumbnail(file)) continue;
    scheduleThumbnailGeneration(id, file);
  }
}

export function clearMediaUploadThumbnailCache() {
  for (const fileId of [...cache.keys()]) {
    releaseEntry(fileId);
  }
}

export async function awaitMediaUploadThumbnail(
  fileId: string,
  signal?: AbortSignal,
): Promise<MediaUploadThumbnailResult | null> {
  const entry = cache.get(fileId);
  if (!entry) {
    return null;
  }

  if (entry.status === 'ready') {
    return entry.result;
  }

  if (entry.status === 'error') {
    return null;
  }

  return new Promise<MediaUploadThumbnailResult | null>((resolve, reject) => {
    const onAbort = () => {
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    if (signal?.aborted) {
      onAbort();
      return;
    }

    signal?.addEventListener('abort', onAbort, { once: true });

    entry.waiters.push({
      resolve: (value) => {
        signal?.removeEventListener('abort', onAbort);
        resolve(value);
      },
      reject: (reason) => {
        signal?.removeEventListener('abort', onAbort);
        reject(reason);
      },
    });
  });
}
